// src/pages/rapim/RapimPresentasi.tsx â€” MODERN IMMERSIVE REDESIGN
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Maximize2, Minimize2,
  FileText, Target, Users, Clock, MapPin, Calendar,
  CheckCircle, AlertTriangle, TrendingUp,
  Building2, Zap, BarChart3, Play, Pause, Grid3X3} from 'lucide-react';
import { LogoLoadingMark } from '../../components/ui';
import { rapimService } from '../../services/rapim';
import { RapatPimpinan } from '../../types';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';

// ========== TYPES ==========
interface Slide {
  id: number;
  type: 'cover' | 'overview' | 'laporan' | 'summary' | 'keputusan' | 'closing';
  title: string;
  subtitle?: string;
  data?: any;
}

type RiskSummary = {
  total: number;
  kritis: number;
  tinggi: number;
  sedang: number;
  rendah: number;
};

// ========== CONSTANTS ==========
const RISK_COLORS = {
  kritis: { bg: 'bg-red-500', text: 'text-red-400', glow: 'shadow-red-500/50' },
  tinggi: { bg: 'bg-orange-500', text: 'text-orange-400', glow: 'shadow-orange-500/50' },
  sedang: { bg: 'bg-yellow-500', text: 'text-yellow-400', glow: 'shadow-yellow-500/50' },
  rendah: { bg: 'bg-green-500', text: 'text-green-400', glow: 'shadow-green-500/50' }};

const buildRiskSummary = (laporanList: Array<{ tingkat_risiko?: string | null }>): RiskSummary => ({
  total: laporanList.length,
  kritis: laporanList.filter((l) => ['kritis', 'critical'].includes(String(l.tingkat_risiko || '').toLowerCase())).length,
  tinggi: laporanList.filter((l) => ['tinggi', 'high'].includes(String(l.tingkat_risiko || '').toLowerCase())).length,
  sedang: laporanList.filter((l) => ['sedang', 'medium'].includes(String(l.tingkat_risiko || '').toLowerCase())).length,
  rendah: laporanList.filter((l) => ['rendah', 'low'].includes(String(l.tingkat_risiko || '').toLowerCase())).length,
});

// ========== SUB-COMPONENTS ==========

// Animated Background
const AnimatedBackground: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1a] via-[#0d1829] to-[#0f2027]" />

    {/* Animated Orbs */}
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />

    {/* Grid Pattern */}
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'}}
    />

    {/* Floating Particles */}
    <div className="absolute inset-0">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${5 + Math.random() * 10}s`}}
        />
      ))}
    </div>
  </div>
);

// Progress Bar
const SlideProgress: React.FC<{ current: number; total: number }> = ({ current, total }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-teal-400 to-teal-500 rounded-full transition-all duration-500"
        style={{ width: `${((current + 1) / total) * 100}%` }}
      />
    </div>
    <span className="text-xs font-mono text-white/40">
      {current + 1}/{total}
    </span>
  </div>
);

// Slide Navigation Dots
const NavigationDots: React.FC<{
  slides: Slide[];
  current: number;
  onSelect: (index: number) => void;
}> = ({ slides, current, onSelect }) => (
  <div className="flex items-center gap-2 justify-center">
    {slides.map((slide, i) => {
      const isActive = i === current;
      const isPast = i < current;

      return (
        <button
          key={slide.id}
          onClick={() => onSelect(i)}
          className="group relative"
          title={slide.title}
        >
          <div className={`
            w-2.5 h-2.5 rounded-full transition-all duration-300
            ${isActive
              ? 'w-8 bg-gradient-to-r from-teal-400 to-teal-500'
              : isPast
                ? 'bg-teal-500/50'
                : 'bg-white/20 hover:bg-white/40'}
          `} />

          {/* Tooltip */}
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg text-[10px] text-white whitespace-nowrap">
              {slide.title}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

// Timer Component
const PresentationTimer: React.FC<{ startTime: Date }> = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;

  return (
    <div className="flex items-center gap-1.5 text-white/40 text-xs font-mono">
      <Clock className="w-3.5 h-3.5" />
      {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </div>
  );
};

// Slide Thumbnail Grid
const ThumbnailGrid: React.FC<{
  slides: Slide[];
  current: number;
  onSelect: (index: number) => void;
  onClose: () => void;
}> = ({ slides, current, onSelect, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-8">
    <button
      onClick={onClose}
      className="absolute top-6 right-6 p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
    >
      <X className="w-6 h-6" />
    </button>

    <div className="grid grid-cols-4 gap-4 max-w-5xl w-full max-h-[80vh] overflow-y-auto">
      {slides.map((slide, i) => (
        <button
          key={slide.id}
          onClick={() => { onSelect(i); onClose(); }}
          className={`
            aspect-video rounded-xl border-2 p-4 text-left transition-all
            ${i === current
              ? 'border-teal-500 bg-teal-500/20'
              : 'border-white/10 bg-white/5 hover:border-white/30'}
          `}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-mono text-white/40">{i + 1}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold ${slide.type === 'cover' ? 'bg-purple-500/20 text-purple-300' :
              slide.type === 'laporan' ? 'bg-blue-500/20 text-blue-300' :
                slide.type === 'summary' ? 'bg-amber-500/20 text-amber-300' :
                  'bg-teal-500/20 text-teal-300'
              }`}>
              {slide.type}
            </span>
          </div>
          <p className="text-sm text-white font-semibold line-clamp-2">{slide.title}</p>
        </button>
      ))}
    </div>
  </div>
);

// ========== SLIDE RENDERERS ==========

// Cover Slide
const CoverSlide: React.FC<{
  data: any;
  title: string;
  logoUrl?: string;
  institutionName: string;
  brandInitials: string;
  onLogoError: () => void;
}> = ({ data, title, logoUrl, institutionName, brandInitials, onLogoError }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8">
    {/* Logo */}
    <div className="relative mb-8">
      <div
        className={`w-24 h-24 rounded-3xl flex items-center justify-center ${
          logoUrl
            ? 'bg-transparent shadow-none'
            : 'bg-gradient-to-br from-teal-400 to-teal-600 shadow-2xl shadow-teal-500/30'
        }`}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo institusi"
            className="w-full h-full object-contain drop-shadow-[0_12px_28px_rgba(45,212,191,0.28)]"
            onError={onLogoError}
          />
        ) : (
          <span className="text-4xl font-black text-white">{brandInitials}</span>
        )}
      </div>
      {!logoUrl && (
        <div className="absolute -inset-4 bg-teal-500/20 rounded-[2rem] blur-xl -z-10 animate-pulse" />
      )}
    </div>

    {/* Title */}
    <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-4 max-w-4xl leading-tight">
      {title}
    </h1>
    <p className="text-xl text-white/50 mb-8">
      {`Rapat Pimpinan • ${institutionName}`}
    </p>

    {/* Meta Cards */}
    <div className="flex items-center gap-6 flex-wrap justify-center">
      {[
        { icon: Calendar, label: data.tanggal, color: 'from-blue-400 to-blue-500' },
        { icon: MapPin, label: data.tempat, color: 'from-rose-400 to-rose-500' },
        { icon: Users, label: `${data.peserta} Peserta`, color: 'from-amber-400 to-amber-500' },
        { icon: FileText, label: `${data.agenda} Agenda`, color: 'from-emerald-400 to-emerald-500' },
      ].map((item, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-5 py-3 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
        >
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
            <item.icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm text-white/80 font-medium">{item.label}</span>
        </div>
      ))}
    </div>

    {/* Start Hint */}
    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
      <span className="text-xs text-white/30 uppercase tracking-widest">Tekan spasi atau panah kanan</span>
      <ChevronRight className="w-5 h-5 text-white/30" />
    </div>
  </div>
);

// Overview Slide
const OverviewSlide: React.FC<{ data: any }> = ({ data }) => (
  <div className="flex flex-col items-center justify-center h-full px-12">
    <h2 className="text-4xl font-black text-white mb-12">Ringkasan Laporan</h2>

    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6 w-full max-w-6xl mb-12">
      {[
        { label: 'Total Laporan', value: data.total, gradient: 'from-teal-400 to-teal-500', icon: FileText },
        { label: 'Kritis', value: data.kritis, gradient: 'from-red-400 to-red-500', icon: AlertTriangle },
        { label: 'Tinggi', value: data.tinggi, gradient: 'from-orange-400 to-orange-500', icon: Zap },
        { label: 'Sedang', value: data.sedang, gradient: 'from-yellow-400 to-yellow-500', icon: TrendingUp },
        { label: 'Rendah', value: data.rendah, gradient: 'from-green-400 to-green-500', icon: CheckCircle },
      ].map((stat, i) => (
        <div
          key={i}
          className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6"
        >
          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.gradient}`} />
          <stat.icon className="w-8 h-8 text-white/30 mb-4" />
          <p className="text-4xl font-black text-white mb-1">{stat.value}</p>
          <p className="text-sm text-white/50">{stat.label}</p>
        </div>
      ))}
    </div>

    {/* Unit Distribution */}
    {data.unitTerdampak?.length > 0 && (
      <div className="text-center">
        <p className="text-xs text-white/30 uppercase tracking-widest mb-4">Unit Terdampak</p>
        <div className="flex items-center gap-3 justify-center flex-wrap">
          {data.unitTerdampak.map((unit: string, i: number) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl">
              <Building2 className="w-4 h-4 text-teal-400" />
              <span className="text-sm text-white/80">{unit}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Laporan Detail Slide
const LaporanSlide: React.FC<{ data: any; slideNumber: number; total: number }> = ({ data, slideNumber, total }) => {
  const riskColor = RISK_COLORS[data.risiko as keyof typeof RISK_COLORS] || RISK_COLORS.sedang;

  return (
    <div className="flex h-full">
      {/* Left Panel - Main Info */}
      <div className="flex-1 flex flex-col justify-center px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm font-mono text-teal-400 bg-teal-400/10 px-4 py-2 rounded-xl">
            {data.kode}
          </span>
          <span className={`
            px-4 py-2 rounded-xl text-sm font-bold uppercase
            ${riskColor.bg}/20 ${riskColor.text}
          `}>
            Risiko {data.risiko}
          </span>
          <span className="text-sm text-white/30 ml-auto">
            Laporan {slideNumber} dari {total}
          </span>
        </div>

        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-8">
          {data.judul}
        </h2>

        {/* Description */}
        <div className="space-y-6">
          <div>
            <p className="text-xs text-white/30 uppercase tracking-widest mb-2">Deskripsi Masalah</p>
            <p className="text-base text-white/80 leading-relaxed">{data.deskripsi}</p>
          </div>

          <div>
            <p className="text-xs text-white/30 uppercase tracking-widest mb-2">Dampak</p>
            <p className="text-base text-white/80 leading-relaxed">{data.dampak}</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Details */}
      <div className="w-96 bg-white/5 backdrop-blur-sm border-l border-white/10 p-8 flex flex-col justify-center">
        <div className="space-y-6">
          {/* Akar Masalah */}
          <div className="p-5 bg-white/5 rounded-2xl">
            <p className="text-xs text-white/30 uppercase tracking-widest mb-2">Akar Masalah</p>
            <p className="text-sm text-white/90 leading-relaxed">{data.akar_masalah}</p>
          </div>

          {/* Meta Info */}
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
              <Building2 className="w-5 h-5 text-teal-400" />
              <div>
                <p className="text-[10px] text-white/30 uppercase">Unit</p>
                <p className="text-sm text-white font-semibold">{data.unit}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
              <Users className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-[10px] text-white/30 uppercase">Pelapor</p>
                <p className="text-sm text-white font-semibold">{data.pelapor}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
              <Target className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-[10px] text-white/30 uppercase">Kategori</p>
                <p className="text-sm text-white font-semibold">{data.kategori}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Summary Stats Slide
const SummarySlide: React.FC<{ data: any }> = ({ data }) => (
  <div className="flex flex-col items-center justify-center h-full px-12">
    <h2 className="text-4xl font-black text-white mb-4">Statistik & Analisis</h2>
    <p className="text-white/50 mb-12 text-center max-w-lg">
      Ringkasan keseluruhan laporan yang dibahas dalam rapat pimpinan
    </p>

    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6 w-full max-w-6xl">
      {[
        { label: 'Total Laporan', value: data.total, color: 'teal', icon: FileText },
        { label: 'Kritis', value: data.kritis, color: 'red', icon: AlertTriangle },
        { label: 'Tinggi', value: data.tinggi, color: 'orange', icon: Zap },
        { label: 'Sedang', value: data.sedang, color: 'yellow', icon: TrendingUp },
        { label: 'Rendah', value: data.rendah, color: 'green', icon: CheckCircle },
      ].map((stat, i) => (
        <div
          key={i}
          className="relative group"
        >
          <div className={`
            absolute inset-0 bg-${stat.color}-500/20 rounded-3xl blur-xl 
            group-hover:blur-2xl transition-all
          `} />
          <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 text-center">
            <stat.icon className={`w-10 h-10 mx-auto mb-4 text-${stat.color}-400`} />
            <p className="text-5xl font-black text-white mb-2">{stat.value}</p>
            <p className="text-sm text-white/50">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Chart Placeholder */}
    <div className="mt-12 flex items-center gap-8 text-white/30">
      <BarChart3 className="w-6 h-6" />
      <span className="text-sm">Distribusi berdasarkan tingkat risiko</span>
    </div>
  </div>
);

// Closing Slide
const ClosingSlide: React.FC<{ data: any; institutionName: string }> = ({ data, institutionName }) => (
  <div className="flex flex-col items-center justify-center h-full px-12 text-center">
    {/* Success Icon */}
    <div className="relative mb-8">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <div className="absolute -inset-4 bg-emerald-500/20 rounded-full blur-xl -z-10 animate-pulse" />
    </div>

    <h2 className="text-4xl font-black text-white mb-4">Tindak Lanjut</h2>
    <p className="text-white/50 mb-12 max-w-lg">
      Langkah-langkah yang perlu dilakukan setelah rapat pimpinan
    </p>

    {/* Next Steps */}
    <div className="space-y-4 w-full max-w-xl">
      {data.nextSteps?.map((step: string, i: number) => (
        <div
          key={i}
          className="flex items-center gap-4 p-5 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
            {i + 1}
          </div>
          <p className="text-white/90 font-medium">{step}</p>
        </div>
      ))}
    </div>

    {/* Footer */}
    <div className="mt-16 flex flex-col items-center gap-4">
      <p className="text-white/20 text-sm">Terima kasih atas perhatiannya</p>
      <div className="flex items-center gap-2 text-white/30 text-xs">
        <span>SIM-EVAL</span>
        <span>•</span>
        <span>{institutionName}</span>
      </div>
    </div>
  </div>
);

// ========== MAIN COMPONENT ==========
const RapimPresentasi: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings, fetchPublicSettings } = useSystemSettingsStore();

  // State
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [startTime] = useState(new Date());
  const [isPlaying, setIsPlaying] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  // Auto-play interval
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        if (prev >= slides.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 10000); // 10 seconds per slide

    return () => clearInterval(interval);
  }, [isPlaying, slides.length]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await rapimService.getPresentationData(Number(id));
        if (res.success && res.data) {
          generateSlides(res.data.rapat, res.data.summary);
        } else {
          const rapat = (res as any)?.data || res;
          generateSlides(rapat);
        }
      } catch (e) {
        console.error(e);
        toast.error('Gagal memuat data presentasi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateSlides = (rapat: RapatPimpinan, summary?: any) => {
    const generatedSlides: Slide[] = [];
    const laporanList = rapat.laporan || rapat.laporan_masalah || [];
    const riskSummary = buildRiskSummary(laporanList as Array<{ tingkat_risiko?: string | null }>);

    // 1. Cover
    generatedSlides.push({
      id: 1,
      type: 'cover',
      title: rapat.judul,
      data: {
        tanggal: new Date(rapat.tanggal).toLocaleDateString('id-ID', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        }),
        tempat: rapat.tempat || 'Online / Tidak ditentukan',
        peserta: rapat.peserta?.length || 0,
        agenda: laporanList.length}});

    // 2. Overview (if we have reports)
    if (laporanList.length > 0) {
      const units = Array.from(new Set(laporanList.map(l => l.unit?.name).filter(Boolean)));
      generatedSlides.push({
        id: 2,
        type: 'overview',
        title: 'Ringkasan Laporan',
        data: {
          ...riskSummary,
          unitTerdampak: units.slice(0, 5)}});
    }

    // 3. Individual Reports
    laporanList.forEach((lap, index) => {
      const laporanData = lap as any;
      generatedSlides.push({
        id: generatedSlides.length + 1,
        type: 'laporan',
        title: lap.judul.length > 50 ? lap.judul.substring(0, 50) + '...' : lap.judul,
        subtitle: lap.kode,
        data: {
          kode: lap.kode,
          judul: lap.judul,
          unit: lap.unit?.name || '-',
          pelapor: laporanData.user?.name || laporanData.pelapor?.name || '-',
          risiko: lap.tingkat_risiko,
          deskripsi: lap.deskripsi || '-',
          dampak: lap.dampak || '-',
          akar_masalah: lap.akar_masalah || '-',
          kategori: laporanData.kategori_masalah?.nama || '-'}});
    });

    // 4. Summary Stats
    if (laporanList.length > 0) {
      generatedSlides.push({
        id: generatedSlides.length + 1,
        type: 'summary',
        title: 'Statistik & Analisis',
        data: {
          ...riskSummary}});
    }

    // 5. Closing
    generatedSlides.push({
      id: generatedSlides.length + 1,
      type: 'closing',
      title: 'Penutup',
      data: {
        nextSteps: [
          'Monitoring pelaksanaan keputusan yang telah disepakati',
          'Distribusi notulen rapat kepada seluruh peserta',
          'Update progress setiap 2 minggu melalui sistem',
          'Evaluasi berkala pada rapat berikutnya',
        ]}});

    setSlides(generatedSlides);
  };

  // Navigation
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'Escape':
          if (showThumbnails) {
            setShowThumbnails(false);
          } else {
            navigate(`/rapim/${id}`);
          }
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'g':
        case 'G':
          setShowThumbnails(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, navigate, id, showThumbnails]);

  const exitPresentation = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    navigate(`/rapim/${id}`);
  };

  // Current slide data
  const slide = slides[currentSlide];
  const laporanSlideNumber = useMemo(() => {
    if (slide?.type !== 'laporan') return 0;
    return slides.filter((s, i) => s.type === 'laporan' && i <= currentSlide).length;
  }, [slides, currentSlide, slide?.type]);

  const totalLaporanSlides = useMemo(() =>
    slides.filter(s => s.type === 'laporan').length,
    [slides]
  );

  const institutionName = settings.institution_name || settings.app_name || 'SIM-EVAL';
  const logoUrl = logoError ? undefined : getAssetUrl(settings.logo_url);
  const brandInitials = (settings.app_name || settings.institution_name || 'SIM-EVAL')
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Render slide content
  const renderSlide = () => {
    if (!slide) return null;

    switch (slide.type) {
      case 'cover':
        return (
          <CoverSlide
            data={slide.data}
            title={slide.title}
            logoUrl={logoUrl}
            institutionName={institutionName}
            brandInitials={brandInitials}
            onLogoError={() => setLogoError(true)}
          />
        );
      case 'overview':
        return <OverviewSlide data={slide.data} />;
      case 'laporan':
        return <LaporanSlide data={slide.data} slideNumber={laporanSlideNumber} total={totalLaporanSlides} />;
      case 'summary':
        return <SummarySlide data={slide.data} />;
      case 'closing':
        return <ClosingSlide data={slide.data} institutionName={institutionName} />;
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <LogoLoadingMark size="lg" forceShowLogo />
          </div>
          <p className="text-sm text-white/50">Mempersiapkan presentasi...</p>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 bg-[#0a0f1a] flex flex-col items-center justify-center text-white">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <p className="text-lg font-semibold mb-2">Tidak Ada Data</p>
        <p className="text-white/50 mb-6">Presentasi tidak dapat dibuat karena tidak ada data rapat</p>
        <button
          onClick={() => navigate(`/rapim/${id}`)}
          className="px-6 py-2 bg-teal-500 text-white rounded-xl font-semibold hover:bg-teal-600 transition-colors"
        >
          Kembali ke Detail Rapat
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      <AnimatedBackground />

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-6 z-20 bg-gradient-to-b from-black/30 to-transparent">
        <div className="flex items-center gap-4">
          <PresentationTimer startTime={startTime} />
          <div className="w-px h-4 bg-white/10" />
          <span className="text-xs text-white/40">
            {slide?.type === 'laporan' ? `Laporan ${laporanSlideNumber}/${totalLaporanSlides}` : slide?.title}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            title={isPlaying ? 'Pause auto-play' : 'Auto-play (10s)'}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setShowThumbnails(true)}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            title="Lihat semua slide (G)"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            title="Fullscreen (F)"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={exitPresentation}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            title="Keluar (ESC)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="absolute inset-0 pt-14 pb-20">
        {renderSlide()}
      </div>

      {/* Bottom Navigation */}
      <div className="absolute bottom-0 left-0 right-0 h-20 flex flex-col justify-center gap-4 px-6 z-20 bg-gradient-to-t from-black/30 to-transparent">
        <SlideProgress current={currentSlide} total={slides.length} />

        <div className="flex items-center justify-between">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-5 h-5" /> Sebelumnya
          </button>

          <NavigationDots
            slides={slides}
            current={currentSlide}
            onSelect={goToSlide}
          />

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Selanjutnya <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Keyboard Hints */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-white/20 z-20">
        Panah kiri/kanan untuk navigasi • Spasi untuk lanjut • G untuk grid • F untuk fullscreen • ESC untuk keluar
      </div>

      {/* Thumbnail Grid Modal */}
      {showThumbnails && (
        <ThumbnailGrid
          slides={slides}
          current={currentSlide}
          onSelect={goToSlide}
          onClose={() => setShowThumbnails(false)}
        />
      )}
    </div>
  );
};

export default RapimPresentasi;

